import * as React from 'react';
import Table from 'react-bootstrap/Table';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import Pagination from '@mui/material/Pagination';
import { NavLink } from 'react-router-dom';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { postRequest } from '../../../lib/api';
import { AuthContext } from '../../../authContext/context';
import axiosInstance from '../../../lib/axiosIntercepter';
import { useContext, useEffect, useRef, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Swal from 'sweetalert2';
import Tooltip from '@mui/material/Tooltip';
import Dropdown from 'react-bootstrap/Dropdown';
import AddIcon from '@mui/icons-material/Add';
import FormControl from '@mui/material/FormControl';
import TablePagination from '@mui/material/TablePagination';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/Search';
import { Link, useNavigate } from 'react-router-dom';
import { checkPermission } from '../../../utils/checkPermissions';
import PermissionWrapper from '../../../components/PermissionWrapper';

export default function EmailTemplates() {
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const userPermissions = authCtx.permissions;
  
  // Check permission
  const canManageEmailTemplates = checkPermission(userPermissions, 'settings', 'manage_email_templates');
  
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0
  });

  // Search/Filter states
  const [filters, setFilters] = useState({
    search: '',
    productId: '',
    templateType: ''
  });
  console.log('sadas')

  // Dropdown options
  const [dropdownOptions, setDropdownOptions] = useState({
    products: []
  });
  console.log('sadasfasfasfas')

  // Fetch email templates
  const fetchTemplates = async (page = 1, searchFilters = filters) => {
    try {
      console.log('🔍 Fetching templates with:', { page, searchFilters, token: authCtx.token ? 'exists' : 'missing' });
      setLoading(true);

      const response = await axiosInstance.post('private/email-templates', {
        page,
        limit: 10,
        search: searchFilters.search,
        productId: searchFilters.productId || undefined,
        templateType: searchFilters.templateType || undefined,
        isActive: 'true'
      });

      const data = response.data;
      console.log('📊 API Response data:', data);

      if (data.status === 200) {
        console.log('✅ Templates loaded:', data.data.templates.length);
        setTemplates(data.data.templates);
        setPagination(data.data.pagination);
      } else {
        console.log('❌ API Error:', data);
        throw new Error(data.message || 'Failed to fetch templates');
      }
    } catch (error) {
      console.error('❌ Error fetching templates:', error);
      setTemplates([]);
      // Silently handle the error without showing popup
    } finally {
      setLoading(false);
    }
  };

  // Fetch dropdown options
  const fetchDropdownOptions = async () => {
    try {
      const response = await axiosInstance.get('private/email-templates-options');
      const data = response.data;

      if (data.status === 200) {
        setDropdownOptions({
          products: data.data.products
        });
      }
    } catch (error) {
      console.error('Error fetching dropdown options:', error);
    }
  };

  useEffect(() => {
    if (authCtx.token) {
      fetchTemplates();
      fetchDropdownOptions();
    }
  }, [authCtx.token]);

  // Handle search
  const handleSearch = () => {
    fetchTemplates(1, filters);
  };

  // Handle page change
  const handlePageChange = (event, value) => {
    fetchTemplates(value, filters);
  };

  // Handle input changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle delete template
  const handleDelete = async (templateId, templateName) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `This will delete the template "${templateName}"`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        const response = await axiosInstance.delete(`private/email-templates/${templateId}`);
        const data = response.data;

        if (data.status === 200) {
          Swal.fire('Deleted!', 'Email template has been deleted.', 'success');
          fetchTemplates(pagination.currentPage, filters);
        } else {
          throw new Error(data.message || 'Failed to delete template');
        }
      } catch (error) {
        Swal.fire('Error', error.message || 'Failed to delete email template', 'error');
      }
    }
  };

  // Handle edit template
  const handleEdit = async (templateId) => {
    try {
      console.log('🔧 Fetching template details for:', templateId);

      // Fetch template details to check type
      const response = await axiosInstance.get(`private/email-templates/${templateId}`);
      const data = response.data;
      console.log('✅ Template data:', data);

      if (data.status === 200) {
        const template = data.data;
        // Navigate to appropriate editor based on template type
        if (template.type === 'Advanced' || template.isAdvanced) {
          navigate(`/emailtemplates/edit/advanced/${templateId}`);
        } else {
          navigate(`/emailtemplates/edit/${templateId}`);
        }
      } else {
        throw new Error(data.message || 'Failed to fetch template details');
      }
    } catch (error) {
      console.error('❌ Error fetching template:', error);

      // Show more specific error message
      let errorMessage = 'Failed to load template for editing';
      if (error.message.includes('Proxy error') || error.message.includes('non-JSON')) {
        errorMessage = 'Backend server is not responding correctly. Please check if the backend is running.';
      } else if (error.message.includes('Unexpected token')) {
        errorMessage = 'Server returned invalid data. Please contact administrator.';
      }

      Swal.fire({
        icon: 'error',
        title: 'Error Loading Template',
        text: errorMessage,
        footer: `<small>Technical details: ${error.message}</small>`
      });
    }
  };


  return (
    <PermissionWrapper
      category="settings"
      permission="manage_email_templates"
      pageTitle="Email Templates"
      breadcrumbs={[
        { label: 'Dashboard', path: '/dashboard' },
        { label: 'Email Templates', path: '' },
      ]}
    >
      <>
        <div className='page-header pb-3'>
          <h1 className='mt-0 mb-0'>Email Templates</h1>
          {canManageEmailTemplates && (
            <div className='header-right-btn'>
              <Stack spacing={2} direction="row">
            {/* <Button variant="outlined" className='red-outline-btn text-capitalize'>
              <NavLink to="/emailtemplates/merge-fields-explorer" className="red-link-text">
                View Merge Fields
              </NavLink>
            </Button> */}
           
            <Dropdown>
              <Dropdown.Toggle className='red-outline-btn text-capitalize bg-transparent'>
                <AddIcon /> &nbsp; Create Email Template
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item as={Link} to="/emailtemplates/createbasic">Basic</Dropdown.Item>   
                <Dropdown.Item as={Link} to="/emailtemplates/createadvanced">Advanced</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
              </Stack>
            </div>
          )}
        </div>

      {/* Search Form */}
      <div className='page-top-formview mb-3'>
        <Form>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="searchProduct">
              <FormControl sx={{ my: 1, width: '100%' }} variant="outlined">
                <InputLabel id="product-select-label">Product</InputLabel>
                <Select
                  labelId="product-select-label"
                  id="product-select"
                  value={filters.productId}
                  label="Product"
                  onChange={(e) => handleFilterChange('productId', e.target.value)}
                >
                  <MenuItem value="">All Products</MenuItem>
                  {dropdownOptions.products.map((product) => (
                    <MenuItem key={product._id} value={product._id}>
                      {product.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Form.Group>
            
            <Form.Group as={Col} controlId="searchName">
              <FormControl sx={{ my: 1, width: '100%' }} variant="outlined">
                <TextField 
                  id="template-name-search" 
                  label="Template Name" 
                  variant="outlined" 
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
              </FormControl>
            </Form.Group>
            
            <Form.Group as={Col} controlId="searchType">
              <FormControl sx={{ my: 1, width: '100%' }} variant="outlined">
                <InputLabel id="template-type-select-label">Template Type</InputLabel>
                <Select
                  labelId="template-type-select-label"
                  id="template-type-select"
                  value={filters.templateType}
                  label="Template Type"
                  onChange={(e) => handleFilterChange('templateType', e.target.value)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="Basic">Basic</MenuItem>
                  <MenuItem value="Advanced">Advanced</MenuItem>
                </Select>
              </FormControl>
            </Form.Group>
            
            <Form.Group as={Col} controlId="searchButton">
              <FormControl sx={{ my: 1, width: '100%' }} variant="outlined">
                <Stack spacing={2} direction="row">
                  <Button 
                    variant="contained" 
                    onClick={handleSearch}
                    className='red-bg red-button text-capitalize h-56'
                  >
                    <SearchIcon/> &nbsp; Search
                  </Button>
                </Stack>
              </FormControl>
            </Form.Group>
          </Row>
        </Form> 

        <div className='customer-tab-view'> 
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
              <CircularProgress />
            </div>
          ) : (
            <>
              <Table striped responsive bordered>
                <thead>
                  <tr>
                    <th colSpan={6} className='bg-grey'>Email Templates</th>
                  </tr>
                  <tr>
                    <th>Template Name</th>
                    <th>Product</th>
                    <th>Subject</th>
                    <th>Type</th>
                    <th>Theme</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.length === 0 ? (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', padding: '50px' }}>
                        No email templates found
                      </td>
                    </tr>
                  ) : (
                    templates.map((template) => (
                      <tr key={template._id}>
                        <td>
                          <strong>{template.name}</strong>
                          {template.description && (
                            <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                              {template.description}
                            </div>
                          )}
                        </td>
                        <td>{template.productId?.title || 'N/A'}</td>
                        <td>{template.subject}</td>
                        <td>
                          <span 
                            style={{
                              display: 'inline-block',
                              padding: '4px 12px',
                              borderRadius: '4px',
                              backgroundColor: template.type === 'Advanced' ? '#7c3aed' : '#3b82f6',
                              color: '#fff',
                              fontSize: '0.85em',
                              fontWeight: '500'
                            }}
                          >
                            {template.type || 'Basic'}
                          </span>
                        </td>
                        <td>
                          <span 
                            style={{
                              display: 'inline-block',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              backgroundColor: template.emailThemeId?.headerColor || '#e5e5e5',
                              color: '#fff',
                              fontSize: '0.8em'
                            }}
                          >
                            {template.emailThemeId?.name || 'No Theme'}
                          </span>
                        </td>
                        <td>
                          <Tooltip title="Edit Template">
                            <span 
                              className="red-link-text cursor-pointer"
                              onClick={() => handleEdit(template._id)}
                            >
                              <EditOutlinedIcon/>
                            </span>
                          </Tooltip>
                          &nbsp;&nbsp;&nbsp;
                          <Tooltip title="Delete Template">
                            <span
                              className='red-link-text cursor-pointer w-18'
                              onClick={() => handleDelete(template._id, template.name)}
                            >
                              <DeleteOutlineOutlinedIcon />
                            </span>
                          </Tooltip>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>

              {/* Pagination */}
              {pagination.totalCount > 10 && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 8, marginBottom: 8 }}>
                    <span className="text-muted small">
                      Showing {((pagination.currentPage - 1) * 10) + 1}-{Math.min(pagination.currentPage * 10, pagination.totalCount)} of {pagination.totalCount} email templates
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
                    <Pagination
                      count={pagination.totalPages}
                      page={pagination.currentPage}
                      onChange={handlePageChange}
                      color="primary"
                      shape="rounded"
                      size="medium"
                      sx={{
                        '& .Mui-selected': {
                          background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%) !important',
                          color: '#fff !important',
                        },
                        '& .MuiPaginationItem-root': {
                          color: 'var(--bs-red-color)',
                          borderColor: 'var(--bs-red-color)',
                        },
                      }}
                    />
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
      </>
    </PermissionWrapper>
  );
}